import React, { useContext, useState } from 'react';

import { Box, Chip, Divider, makeStyles, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import CollapsableCard from 'components/Generic/CollapsableCard';

import Loading from 'components/Loading';
import client from 'services/client';
import theme from 'theme';
import { notify } from 'components/Notifier';
import { useStreams } from 'hooks';
import { Event, UserRole } from 'types';

import { AppContext } from 'context';
import CreateButton from 'components/Buttons/CreateButton';
import StreamCreateForm from './StreamCreateForm';

const useStyles = makeStyles({
  divider: {
    margin: '10px 34px 10px 11px',
    height: '60%',
  },
  streamName: {
    width: '30%',
  },
  collapsableDivider: {
    margin: '10px 0 8px 0',
  },
  material: {
    marginLeft: '24px',
  },
  materialsList: {
    marginLeft: '19px',
  },
  addButton: {
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 600,
  },
  addButtonEnabled: {
    color: theme.palette.primary.main,
  },
  addButtonDisabled: {
    color: theme.palette.grey[600],
  },
});

const StreamList: React.FC = () => {
  const classes = useStyles();

  const { t } = useTranslation();

  const [streams] = useStreams();

  const [isEditing, setEditing] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const context = useContext(AppContext);
  const roles = context.getRoles?.();
  const sortingCenter = context.getSortingCenter?.();

  const isUserAbleToEdit =
    sortingCenter?.isActive && roles && (roles.includes(UserRole.ROLE_MANAGER) || context.hasLeastOneAdminRole());

  return (
    <>
      {streams !== null ? (
        <>
          {isEditing && (
            <StreamCreateForm
              onCancel={() => {
                setEditing(false);
              }}
              onSubmit={async stream => {
                setEditing(false);
                setIsSubmitting(true);
                try {
                  await client.post('/streams', stream);
                  PubSub.publish(Event.REFRESH_STREAMS);
                  notify({
                    message: t('streams.create.successMessage', { label: stream.name }),
                    severity: 'success',
                  });
                } catch {
                  notify({
                    message: t('errorGeneric'),
                    severity: 'error',
                  });
                }
                setIsSubmitting(false);
              }}
            />
          )}
          {streams.map(stream => (
            <CollapsableCard
              key={stream['@id']}
              collapsableContent={
                <>
                  <Typography variant="inherit" className={classes.material}>
                    {t('materials.entityName')}
                  </Typography>
                  <Divider className={classes.collapsableDivider} />
                  <Box className={classes.materialsList}>
                    {stream.composition.map(material => (
                      <Chip key={`${stream['@id']}_${material.shortName}`} label={material.shortName} />
                    ))}
                  </Box>
                </>
              }
            >
              <Typography variant="inherit" className={classes.streamName}>
                {stream.name}
              </Typography>
              <Divider className={classes.divider} orientation="vertical" flexItem />
              <Typography variant="inherit" color="primary">
                {`${stream.composition.length} ${t('materials.entityName').toLowerCase()}`}
              </Typography>
            </CollapsableCard>
          ))}
          {isUserAbleToEdit && !isSubmitting && (
            <CreateButton
              disabled={isEditing}
              onClick={() => {
                setEditing(true);
              }}
              icon={null}
              label={t('streams.create.add')}
            />
          )}
        </>
      ) : (
        <Loading />
      )}
    </>
  );
};
export default StreamList;
